import { CheckIcon, PencilIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";
import { FieldLegend } from "@fronterahealth/frontera-ui-components/ui/field";

import { ReportSectionsType, useUpdateReportSectionMutation } from "@api/graphql/types-and-hooks";
import { useCanEditSectionTitle } from "@components/ReportField/capabilities";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider";

interface ReportSectionTitleProps {
  section: ReportSectionsType;
  /** Section is pending, stalled or not: locks inline rename. */
  isEditLocked: boolean;
  /**
   * Show the "AI is generating" indicator. False for a stalled section, which stays
   * edit-locked but must stop implying progress. (PROA-2110)
   */
  showLoading: boolean;
}

// Title display + inline rename. `data-testid`s (section-title-row,
// section-title-input-paragraph, save-section-title-button) and the
// "AI is generating this section..." indicator classes match the legacy
// SectionWidgetV2 markup. The end-to-end tests in frontera-tests (Playwright) wait for the indicator text,
// so change it together with them.
export const ReportSectionTitle: FC<ReportSectionTitleProps> = ({ section, isEditLocked, showLoading }) => {
  const { reportQuery } = useReport();
  const canEditTitle = useCanEditSectionTitle(section);
  const updateSection = useUpdateReportSectionMutation({});
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(section.title ?? "");

  const startEditing = () => {
    setTitle(section.title ?? "");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setTitle(section.title ?? "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const data = await updateSection.mutateAsync({ reportSectionId: section.id ?? "", title });
      if (data.updateReportSection?.status) await reportQuery?.refetch();
      setIsEditing(false);
    } catch (error) {
      console.error("Error while updating section title: ", error);
      notifyError("Error Updating Section Title");
    }
  };

  return (
    <FieldLegend variant="label" data-testid="section-title-row" className="flex items-center gap-2">
      {isEditing ? (
        <span className="flex w-full items-center gap-2">
          <input
            className="peer block w-full border-0 py-1.5 text-text-primary ring-inset ring-limestone-200 focus:ring-0 focus:outline-hidden"
            type="text"
            value={title}
            placeholder="Enter section title"
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
          <XMarkIcon
            className="h-6 w-6 cursor-pointer rounded-md border border-limestone-200 p-1 text-text-secondary"
            onClick={cancelEditing}
          />
          <CheckIcon
            data-testid="save-section-title-button"
            className="h-6 w-6 cursor-pointer p-1 text-text-secondary"
            onClick={handleSave}
          />
        </span>
      ) : (
        <>
          <Paragraph
            displayType="loud"
            colorType="primary"
            data-dynamic-section
            data-testid="section-title-input-paragraph"
            className={canEditTitle && !isEditLocked ? "cursor-pointer" : ""}
            onClick={() => canEditTitle && !isEditLocked && startEditing()}
          >
            {section.title}
          </Paragraph>
          {showLoading && (
            <div className="flex items-center space-x-1 text-blurple-500 shimmer">
              <SparklesIcon className="h-6 w-6" />
              <span>AI is generating this section...</span>
            </div>
          )}
          {canEditTitle && !isEditLocked && (
            <PencilIcon
              className="h-6 w-6 cursor-pointer rounded-md border border-limestone-200 p-1 text-text-secondary"
              onClick={startEditing}
            />
          )}
        </>
      )}
    </FieldLegend>
  );
};
